import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { StarterKit } from "@tiptap/starter-kit";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Heading } from "@tiptap/extension-heading";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Link } from "@tiptap/extension-link";
import { ResizableImage } from "../components/ResizableImage";
import { FontSize } from "tiptap-extension-font-size";
import OfficePaste from "@intevation/tiptap-extension-office-paste";
import {
  Box,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  TextField,
  Button,
  Popper,
  Paper,
  Grow,
  CircularProgress,
} from "@mui/material";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import LinkIcon from "@mui/icons-material/Link";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteIcon from "@mui/icons-material/Delete";
import { queryEmailGraphRag, magicEditText } from "../services/ai.service";

// Preserves full inline style + class on <table> (e.g. pasted email signatures).
const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class") || null,
        renderHTML: (attributes) => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
    };
  },
});

const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const StyleParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const StyleHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
}).configure({ levels: [1, 2, 3, 4, 5, 6] });

// Preserves ALL inline styles on <td> and <th> cells (background-color, color,
// padding, width, etc.). Without this TipTap silently strips the style attribute
// because it is not in the default TableCell/TableHeader schema.
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

/** Keeps `data-prospo-signature` on `<hr>` so compose can find the signature boundary after round-trip HTML. */
const ProspoSignatureHorizontalRule = HorizontalRule.extend({
  addAttributes() {
    return {
      dataProspoSignature: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-prospo-signature"),
        renderHTML: (attributes) => {
          if (attributes.dataProspoSignature == null) return {};
          return {
            "data-prospo-signature": String(attributes.dataProspoSignature),
          };
        },
      },
    };
  },
});

// Plugin that renders a purely visual highlight on the magic-toolbar selection range.
// Uses decorations (no document mutation) so onChange never fires for it.
const magicSelectionKey = new PluginKey<{ from: number; to: number } | null>(
  "magicSelection",
);
const MagicSelectionExtension = Extension.create({
  name: "magicSelection",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: magicSelectionKey,
        state: {
          init: () => null,
          apply(tr, prev) {
            const meta = tr.getMeta(magicSelectionKey);
            return meta !== undefined ? meta : prev;
          },
        },
        props: {
          decorations(state) {
            const range = magicSelectionKey.getState(state);
            if (!range || range.from >= range.to) return DecorationSet.empty;
            return DecorationSet.create(state.doc, [
              Decoration.inline(range.from, range.to, {
                style:
                  "background-color: rgba(66, 153, 225, 0.35); border-radius: 2px;",
              }),
            ]);
          },
        },
      }),
    ];
  },
});

const FONT_FAMILIES = [
  "Arial",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Verdana",
  "Trebuchet MS",
];
const FONT_SIZES = [10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36];
const MAX_IMAGE_WIDTH = 600;

// --- Props interface — identical to old QuillEditor for drop-in replacement ---
interface QuillEditorProps {
  editorContent: string;
  setEditorContent: (value: string) => void;
  height?: string;
  toolbarOptions?: any[];
  readOnly?: boolean;
  noBorder?: boolean;
  rounded?: boolean;
  noToolbarBorder?: boolean;
  addSpacing?: boolean;
  clientId?: string;
  enableGraphRag?: boolean;
  enableMagicToolbar?: boolean;
  showFontControls?: boolean;
  /** Content grows with document; parent container handles scrolling (preview panels). */
  growWithContent?: boolean;
}

const QuillEditor: React.FC<QuillEditorProps> = ({
  editorContent,
  setEditorContent,
  height = "100%",
  toolbarOptions,
  readOnly = false,
  noBorder = false,
  rounded = false,
  noToolbarBorder = false,
  addSpacing = false,
  clientId,
  enableGraphRag = false,
  enableMagicToolbar = false,
  showFontControls = false,
  growWithContent = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const highlightInputRef = useRef<HTMLInputElement>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const promptInputRef = useRef<HTMLInputElement | null>(null);

  // Track last HTML set from onUpdate to detect external content changes
  const lastHTMLRef = useRef<string>(editorContent);
  const isApiProcessing = useRef(false);
  const selectedRangeRef = useRef<{ from: number; to: number }>({
    from: 0,
    to: 0,
  });

  // Stable refs so TipTap extensions/callbacks always have current values
  const setEditorContentRef = useRef(setEditorContent);
  setEditorContentRef.current = setEditorContent;
  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;
  const graphRagCallbackRef = useRef<
    ((query: string, commandStart: number, commandEnd: number) => void) | null
  >(null);

  // Table context state
  const [inTable, setInTable] = useState(false);

  // Table grid picker popover state
  const [tableGridAnchor, setTableGridAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [tableGridHovered, setTableGridHovered] = useState({ row: 0, col: 0 });

  // DOM-level font at cursor — fallback when TipTap textStyle has no value (pasted content)
  const [domFont, setDomFont] = useState("");
  const [domFontSize, setDomFontSize] = useState("");

  // Magic toolbar UI state
  const [showMagicToolbar, setShowMagicToolbar] = useState(false);
  const [magicAnchorRect, setMagicAnchorRect] = useState<DOMRect | null>(null);
  // Frozen snapshot of the selection range at the moment the toolbar opens.
  // Using state (not a ref) so it's immune to subsequent selectedRangeRef mutations.
  const [decorationRange, setDecorationRange] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const [promptText, setPromptText] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const stopMic = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  };

  const toggleMic = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    if (isListening) {
      stopMic();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognitionRef.current = recognition;

    recognition.onresult = (event: any) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setPromptText(transcript);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.start();
    setIsListening(true);
  };

  // Stop mic when the magic toolbar is dismissed
  useEffect(() => {
    if (!showMagicToolbar) stopMic();
  }, [showMagicToolbar]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop mic on unmount and when the browser tab is hidden
  useEffect(() => {
    const handleVisibility = () => { if (document.hidden) stopMic(); };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      stopMic();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // toolbarOptions=[] means no toolbar (e.g. SMS); any other value shows toolbar
  const showToolbar = !toolbarOptions || toolbarOptions.length > 0;
  const borderRadius = rounded ? "6px" : "4px";

  // GraphRAG keyboard extension — created once, calls latest callback via ref
  const GraphRagExtension = useMemo(() => {
    if (!enableGraphRag) return null;
    return Extension.create({
      name: "graphRag",
      addKeyboardShortcuts() {
        return {
          Enter: ({ editor }) => {
            if (!clientIdRef.current || isApiProcessing.current) return false;
            const { from } = editor.state.selection;
            const doc = editor.state.doc;

            // Find start of current block to get only the current line text
            let blockStart = 0;
            doc.nodesBetween(0, from, (node, pos) => {
              if (node.isBlock) blockStart = pos + 1;
            });
            const lineText = doc.textBetween(blockStart, from);
            const match = lineText.match(/(?:^|\s)(\/(.+))$/);
            if (!match) return false;

            const fullCommand = match[1];
            const query = match[2];
            const commandStart = from - fullCommand.length;
            isApiProcessing.current = true;
            graphRagCallbackRef.current?.(query, commandStart, from);
            return true;
          },
        };
      },
    });
  }, [enableGraphRag]);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: false,
        horizontalRule: false,
        paragraph: false,
        heading: false,
      }),
      ProspoSignatureHorizontalRule,
      StyleParagraph,
      StyleHeading,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      CustomTable.configure({ resizable: true }),
      CustomTableRow,
      CustomTableHeader,
      CustomTableCell,
      ResizableImage.configure({ inline: true, allowBase64: true }),
      Link.configure({ openOnClick: false }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FontSize as any,
      OfficePaste,
      MagicSelectionExtension,
      ...(GraphRagExtension ? [GraphRagExtension] : []),
    ],
    [GraphRagExtension],
  );

  const editor = useEditor({
    extensions,
    content: editorContent,
    editable: !readOnly,
    onCreate: ({ editor }) => {
      // Capture TipTap's normalised HTML so we don't trigger a false external-change sync
      lastHTMLRef.current = editor.getHTML();
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastHTMLRef.current = html;
      setEditorContentRef.current(html);
    },
    onSelectionUpdate: ({ editor }) => {
      setInTable(
        editor.isActive("tableCell") || editor.isActive("tableHeader"),
      );
      // Read DOM-level computed font at cursor so pasted content's font shows in toolbar
      const domSel = window.getSelection();
      if (domSel && domSel.anchorNode) {
        const el =
          domSel.anchorNode.nodeType === Node.TEXT_NODE
            ? domSel.anchorNode.parentElement
            : (domSel.anchorNode as Element);
        if (el) {
          const cs = window.getComputedStyle(el);
          const rawFamily = cs.fontFamily
            .replace(/['"]/g, "")
            .split(",")[0]
            .trim();
          setDomFont(rawFamily);
          const pxVal = parseFloat(cs.fontSize);
          setDomFontSize(isNaN(pxVal) ? "" : String(Math.round(pxVal)));
        }
      }
      if (!enableMagicToolbar) return;
      const { from, to } = editor.state.selection;
      if (from === to) {
        if (!toolbarRef.current?.contains(document.activeElement)) {
          setShowMagicToolbar(false);
        }
        return;
      }
      // Only show for actual text selections — not image/node clicks
      const selectedText = editor.state.doc.textBetween(from, to, " ").trim();
      if (!selectedText) {
        setShowMagicToolbar(false);
        return;
      }
      // Don't show magic toolbar when selection is inside the signature (after separator <hr>)
      let hrPos = -1;
      let firstHrPos = -1;
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "horizontalRule") return;
        if (firstHrPos === -1) firstHrPos = pos;
        const sig = (node.attrs as { dataProspoSignature?: string | null })
          ?.dataProspoSignature;
        if (sig === "1" && hrPos === -1) hrPos = pos;
      });
      if (hrPos === -1) hrPos = firstHrPos;
      if (hrPos !== -1 && to > hrPos) {
        setShowMagicToolbar(false);
        return;
      }
      selectedRangeRef.current = { from, to };
      if (domSel && domSel.rangeCount > 0) {
        setMagicAnchorRect(domSel.getRangeAt(0).getBoundingClientRect());
        setDecorationRange({ from, to });
        setShowMagicToolbar(true);
      }
    },
  });

  // Wire up the GraphRAG callback (after editor is available)
  useEffect(() => {
    graphRagCallbackRef.current = async (
      query: string,
      commandStart: number,
      commandEnd: number,
    ) => {
      if (!editor) return;
      const thinkingText = "(Thinking...)";
      const thinkingEnd = commandStart + thinkingText.length;
      try {
        editor
          .chain()
          .focus()
          .deleteRange({ from: commandStart, to: commandEnd })
          .insertContentAt(commandStart, thinkingText)
          .run();

        const response: any = await queryEmailGraphRag(clientIdRef.current, query);

        if (response?.output) {
          editor
            .chain()
            .focus()
            .deleteRange({ from: commandStart, to: thinkingEnd })
            .insertContentAt(commandStart, response.output)
            .run();
        } else {
          // Rollback: restore original slash command
          editor
            .chain()
            .focus()
            .deleteRange({ from: commandStart, to: thinkingEnd })
            .insertContentAt(commandStart, `/${query}`)
            .run();
        }
      } catch {
        // Rollback on error
        try {
          editor
            .chain()
            .focus()
            .deleteRange({ from: commandStart, to: thinkingEnd })
            .insertContentAt(commandStart, `/${query}`)
            .run();
        } catch {
          // rollback failed — editor state may be inconsistent, nothing to do
        }
      } finally {
        isApiProcessing.current = false;
        lastHTMLRef.current = editor.getHTML();
        setEditorContentRef.current(editor.getHTML());
      }
    };
  }, [editor]);

  // Sync external content changes (e.g. AI generation resets the whole body)
  useEffect(() => {
    if (!editor) return;
    if (editorContent !== lastHTMLRef.current) {
      lastHTMLRef.current = editorContent;
      editor.commands.setContent(editorContent, { emitUpdate: false });
    }
  }, [editor, editorContent]);

  // Sync readOnly prop
  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // Apply/clear the fake selection decoration when the magic toolbar opens or closes.
  // Uses decorationRange (frozen state snapshot) — not selectedRangeRef — to avoid
  // capturing a stale/wrong range if onSelectionUpdate fires again after the toolbar opens.
  useEffect(() => {
    if (!editor || !enableMagicToolbar) return;
    if (
      showMagicToolbar &&
      decorationRange &&
      decorationRange.from < decorationRange.to
    ) {
      editor.view.dispatch(
        editor.state.tr.setMeta(magicSelectionKey, decorationRange),
      );
    } else {
      editor.view.dispatch(editor.state.tr.setMeta(magicSelectionKey, null));
    }
  }, [showMagicToolbar, decorationRange, editor, enableMagicToolbar]);

  // Click-outside → close magic toolbar
  useEffect(() => {
    if (!enableMagicToolbar) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !wrapperRef.current?.contains(target) &&
        !toolbarRef.current?.contains(target)
      ) {
        setShowMagicToolbar(false);
        setDecorationRange(null);
        setPromptText("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [enableMagicToolbar]);

  // Close table grid picker on outside click
  useEffect(() => {
    if (!tableGridAnchor) return;
    const onDocClick = (e: MouseEvent) => {
      if (!tableGridAnchor.contains(e.target as Node)) {
        setTableGridAnchor(null);
        setTableGridHovered({ row: 0, col: 0 });
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [tableGridAnchor]);

  const handleImageInsert = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const scale =
            img.width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / img.width : 1;
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas
            .getContext("2d")!
            .drawImage(img, 0, 0, canvas.width, canvas.height);
          editor
            ?.chain()
            .focus()
            .setImage({
              src: canvas.toDataURL("image/png"),
              width: canvas.width,
              height: canvas.height,
            } as any)
            .run();
        };
        img.src = e.target!.result as string;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [editor]);

  const handleLinkInsert = useCallback(() => {
    const url = window.prompt("Enter URL:");
    if (!url) return;
    editor?.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  const runMagicEdit = useCallback(async () => {
    if (!editor || !promptText.trim()) return;
    const { from, to } = selectedRangeRef.current;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    setMagicLoading(true);
    try {
      const resp: any = await magicEditText(selectedText, promptText);
      if (resp?.success && resp.edited_content != null) {
        editor
          .chain()
          .focus()
          .deleteRange({ from, to })
          .insertContentAt(from, resp.edited_content)
          .run();
        lastHTMLRef.current = editor.getHTML();
        setEditorContentRef.current(editor.getHTML());
        setShowMagicToolbar(false);
        setDecorationRange(null);
        setPromptText("");
      }
    } catch (err) {
      console.error("Magic edit error", err);
    } finally {
      setMagicLoading(false);
    }
  }, [editor, promptText]);

  if (!editor) return null;

  const currentFont =
    editor.getAttributes("textStyle").fontFamily || domFont || "Arial";
  const currentFontSize =
    editor.getAttributes("textStyle").fontSize?.replace("px", "") ||
    domFontSize ||
    "";
  const currentHeading = editor.isActive("heading", { level: 1 })
    ? "h1"
    : editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
        ? "h3"
        : "p";

  return (
    <Box
      ref={wrapperRef}
      sx={{
        // When height="100%", use flex:1 so it grows correctly inside a flex-column parent
        // (height:100% requires an explicit pixel height on the parent, flex:1 does not)
        ...(height === "100%" && !growWithContent ? { flex: 1 } : { height: growWithContent ? "auto" : height }),
        minHeight: growWithContent ? undefined : "200px",
        display: "flex",
        flexDirection: "column",
        border: noBorder ? "none" : "1px solid #ccc",
        borderRadius,
        overflow: growWithContent ? "visible" : "hidden",
        position: "relative",
        bgcolor: "background.paper",
      }}
    >
      {/* Toolbar — hidden when toolbarOptions=[] or readOnly */}
      {showToolbar && !readOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
            px: 1,
            py: 0.75,
            bgcolor: "#f5f5f5",
            borderBottom: noToolbarBorder ? "none" : "1px solid #ddd",
            borderTopLeftRadius: borderRadius,
            borderTopRightRadius: borderRadius,
            flexShrink: 0,
          }}
        >
          {/* Paragraph / heading selector */}
          <Select
            size="small"
            value={currentHeading}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "p") editor.chain().focus().setParagraph().run();
              else
                editor
                  .chain()
                  .focus()
                  .toggleHeading({
                    level: Number(v.replace("h", "")) as 1 | 2 | 3,
                  })
                  .run();
            }}
            sx={{ fontSize: 13, height: 30, minWidth: 90 }}
          >
            <MenuItem value="p" sx={{ fontSize: 13 }}>
              Normal
            </MenuItem>
            <MenuItem value="h1" sx={{ fontSize: 13, fontWeight: "bold" }}>
              Heading 1
            </MenuItem>
            <MenuItem value="h2" sx={{ fontSize: 13, fontWeight: "bold" }}>
              Heading 2
            </MenuItem>
            <MenuItem value="h3" sx={{ fontSize: 13, fontWeight: "bold" }}>
              Heading 3
            </MenuItem>
          </Select>

          {/* Font family + size (only when showFontControls) */}
          {showFontControls && (
            <>
              <Select
                size="small"
                value={currentFont}
                onChange={(e) =>
                  editor.chain().focus().setFontFamily(e.target.value).run()
                }
                sx={{ fontSize: 13, height: 30, minWidth: 130 }}
              >
                {FONT_FAMILIES.map((f) => (
                  <MenuItem
                    key={f}
                    value={f}
                    sx={{ fontFamily: f, fontSize: 13 }}
                  >
                    {f}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={currentFontSize}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val)
                    (editor.chain().focus() as any)
                      .setFontSize(`${val}px`)
                      .run();
                  else (editor.chain().focus() as any).unsetFontSize().run();
                }}
                renderValue={(v) => (v ? v : "Size")}
                sx={{ fontSize: 13, height: 30, minWidth: 72 }}
              >
                <MenuItem value="">Default</MenuItem>
                {FONT_SIZES.map((s) => (
                  <MenuItem key={s} value={String(s)} sx={{ fontSize: 13 }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Bold">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBold().run()}
              color={editor.isActive("bold") ? "primary" : "default"}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              color={editor.isActive("italic") ? "primary" : "default"}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              color={editor.isActive("underline") ? "primary" : "default"}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Strikethrough">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              color={editor.isActive("strike") ? "primary" : "default"}
            >
              <StrikethroughSIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Text colour (only when showFontControls) */}
          {showFontControls && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Text colour">
                <IconButton
                  size="small"
                  onClick={() => colorInputRef.current?.click()}
                  sx={{ position: "relative" }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: "bold",
                      color: editor.getAttributes("textStyle").color || "#000",
                      lineHeight: 1,
                    }}
                  >
                    A
                  </Box>
                  <input
                    ref={colorInputRef}
                    type="color"
                    defaultValue="#000000"
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                    onChange={(e) =>
                      editor.chain().focus().setColor(e.target.value).run()
                    }
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Highlight colour">
                <IconButton
                  size="small"
                  onClick={() => highlightInputRef.current?.click()}
                  color={editor.isActive("highlight") ? "primary" : "default"}
                  sx={{ position: "relative" }}
                >
                  <BorderColorIcon
                    sx={{
                      fontSize: 18,
                      color:
                        editor.getAttributes("highlight").color || "inherit",
                    }}
                  />
                  <input
                    ref={highlightInputRef}
                    type="color"
                    defaultValue="#FFFF00"
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: 0,
                      height: 0,
                    }}
                    onChange={(e) =>
                      editor
                        .chain()
                        .focus()
                        .toggleHighlight({ color: e.target.value })
                        .run()
                    }
                  />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Bullet list">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              color={editor.isActive("bulletList") ? "primary" : "default"}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ordered list">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              color={editor.isActive("orderedList") ? "primary" : "default"}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Align left">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              color={
                editor.isActive({ textAlign: "left" }) ? "primary" : "default"
              }
            >
              <FormatAlignLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align center">
            <IconButton
              size="small"
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }
              color={
                editor.isActive({ textAlign: "center" }) ? "primary" : "default"
              }
            >
              <FormatAlignCenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align right">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              color={
                editor.isActive({ textAlign: "right" }) ? "primary" : "default"
              }
            >
              <FormatAlignRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Insert link">
            <IconButton
              size="small"
              onClick={handleLinkInsert}
              color={editor.isActive("link") ? "primary" : "default"}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert image">
            <IconButton size="small" onClick={handleImageInsert}>
              <InsertPhotoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert table">
            <IconButton
              size="small"
              onClick={(e) =>
                setTableGridAnchor(tableGridAnchor ? null : e.currentTarget)
              }
              color={tableGridAnchor ? "primary" : "default"}
            >
              <TableChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Table grid picker popover */}
          <Popper
            open={Boolean(tableGridAnchor)}
            anchorEl={tableGridAnchor}
            placement="bottom-start"
            style={{ zIndex: 1400 }}
            transition
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper
                  elevation={3}
                  onMouseLeave={() => setTableGridHovered({ row: 0, col: 0 })}
                  sx={{ p: 1.5, userSelect: "none" }}
                >
                  <Box
                    sx={{
                      fontSize: 12,
                      color: "#444",
                      mb: 1,
                      textAlign: "center",
                      minHeight: 18,
                    }}
                  >
                    {tableGridHovered.row > 0
                      ? `${tableGridHovered.row} × ${tableGridHovered.col} Table`
                      : "Insert table"}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(8, 22px)",
                      gap: "3px",
                    }}
                  >
                    {Array.from({ length: 8 }).map((_, r) =>
                      Array.from({ length: 8 }).map((_, c) => (
                        <Box
                          key={`${r}-${c}`}
                          onMouseEnter={() =>
                            setTableGridHovered({ row: r + 1, col: c + 1 })
                          }
                          onClick={() => {
                            editor
                              .chain()
                              .focus()
                              .insertTable({
                                rows: r + 1,
                                cols: c + 1,
                                withHeaderRow: false,
                              })
                              .run();
                            setTableGridAnchor(null);
                            setTableGridHovered({ row: 0, col: 0 });
                          }}
                          sx={{
                            width: 22,
                            height: 22,
                            border: "1px solid",
                            borderColor:
                              r < tableGridHovered.row &&
                              c < tableGridHovered.col
                                ? "#0078d4"
                                : "#ccc",
                            bgcolor:
                              r < tableGridHovered.row &&
                              c < tableGridHovered.col
                                ? "#dbeafe"
                                : "#fff",
                            cursor: "pointer",
                            borderRadius: "2px",
                            transition:
                              "background-color 0.05s, border-color 0.05s",
                          }}
                        />
                      )),
                    )}
                  </Box>
                </Paper>
              </Grow>
            )}
          </Popper>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Clear formatting">
            <IconButton
              size="small"
              onClick={() =>
                editor.chain().focus().unsetAllMarks().clearNodes().run()
              }
            >
              <FormatClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Table context toolbar — visible only when cursor is inside a table */}
      {showToolbar && !readOnly && inTable && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            px: 1,
            py: 0.25,
            bgcolor: "#eef4ff",
            borderBottom: "1px solid #c8d8f0",
            flexShrink: 0,
          }}
        >
          <Box sx={{ fontSize: 11, color: "#555", mr: 0.5, fontWeight: 500 }}>
            Table:
          </Box>
          <Tooltip title="Add row above">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().addRowBefore().run()}
            >
              <TableRowsIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Row ↑</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Add row below">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              <TableRowsIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Row ↓</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete row">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().deleteRow().run()}
            >
              <DeleteIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Del Row</span>
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Add column before">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().addColumnBefore().run()}
            >
              <ViewColumnIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Col ←</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Add column after">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              <ViewColumnIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Col →</span>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete column">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75 }}
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              <DeleteIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Del Col</span>
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Delete table">
            <IconButton
              size="small"
              sx={{ fontSize: 11, borderRadius: 1, px: 0.75, color: "#c0392b" }}
              onClick={() => editor.chain().focus().deleteTable().run()}
            >
              <DeleteIcon sx={{ fontSize: 14, mr: 0.25 }} />
              <span style={{ fontSize: 11 }}>Delete Table</span>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Editor content area */}
      <Box
        sx={{
          flex: growWithContent ? "0 0 auto" : 1,
          overflow: growWithContent ? "visible" : "auto",
          "& .tiptap": {
            height: growWithContent ? "auto" : "100%",
            outline: "none",
            padding: "12px 16px",
            minHeight: growWithContent ? undefined : 160,
            // Do not set line-height on the whole editor — it overrides pasted / signature table layout.
            "& > p, & > ul, & > ol, & > blockquote, & > pre, & > h1, & > h2, & > h3, & > h4, & > h5, & > h6": {
              lineHeight: addSpacing ? 2.5 : 1.6,
            },
            "& > p": { margin: "0 0 12px" },
            "& table td p, & table th p": {
              margin: 0,
              marginBottom: 0,
              lineHeight: "inherit",
            },
            // Descendant (not direct-child) selector — a <ul> nested inside an <li> is not a
            // direct child of the editor root, so "& > ul" alone never matches it, leaving
            // sub-bullets with no indentation at all. Each nesting level's own padding stacks
            // naturally on top of its parent's, which is what gives nested lists their indent.
            "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 8px" },
            "& table td ul, & table td ol, & table th ul, & table th ol": {
              margin: 0,
              marginBottom: 0,
            },
            "& a": { color: "#1976d2", textDecoration: "underline" },
            "& u": { textDecoration: "underline !important" },
            "& s": { textDecoration: "line-through !important" },
            "& img": {
              maxWidth: "100%",
              verticalAlign: "bottom",
            },
            "& img:not([height]):not([style*='height']):not([style*='Height'])": {
              height: "auto",
            },
            "& img[style*='width']": { maxWidth: "none" },
            "& hr[data-prospo-signature]": { display: "none" },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Magic toolbar Popper */}
      {enableMagicToolbar &&
        showMagicToolbar &&
        magicAnchorRect &&
        (() => {
          const virtualAnchor: any = {
            getBoundingClientRect: () => magicAnchorRect,
          };
          return (
            <Popper
              open={showMagicToolbar}
              anchorEl={virtualAnchor}
              placement="top"
              transition
              modifiers={[
                { name: "preventOverflow", options: { boundary: "viewport" } },
              ]}
              style={{ zIndex: 1400 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    ref={(el: any) => {
                      toolbarRef.current = el;
                    }}
                    className="magic-toolbar"
                    sx={{
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderRadius: "24px",
                      boxShadow: 1,
                      minWidth: 180,
                      border: "3px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        width: { xs: 260, sm: 360, md: 440 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1.25,
                          py: 0.75,
                          borderRight: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <AutoAwesome sx={{ mr: 0.75, color: "#6b7280" }} />
                      </Box>
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder="How should I edit this?"
                        value={promptText}
                        autoComplete="off"
                        onChange={(e) => setPromptText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            runMagicEdit();
                          }
                          if (e.key === "Escape") {
                            setShowMagicToolbar(false);
                            setDecorationRange(null);
                            setPromptText("");
                          }
                        }}
                        inputRef={(el: any) => (promptInputRef.current = el)}
                        InputProps={{
                          disableUnderline: true,
                          sx: { px: 1, py: 0.5, fontSize: "0.95rem" },
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={isListening ? "Stop recording" : "Dictate prompt"}>
                                <IconButton
                                  size="small"
                                  onClick={toggleMic}
                                  disabled={magicLoading}
                                  sx={{
                                    color: isListening ? "error.main" : "primary.main",
                                    p: 0.4,
                                    animation: isListening ? "pulse 1.2s ease-in-out infinite" : "none",
                                    "@keyframes pulse": {
                                      "0%, 100%": { opacity: 1 },
                                      "50%": { opacity: 0.4 },
                                    },
                                  }}
                                >
                                  {isListening
                                    ? <StopCircleOutlinedIcon sx={{ fontSize: 17 }} />
                                    : <MicNoneOutlinedIcon sx={{ fontSize: 17 }} />}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ px: 1, py: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={runMagicEdit}
                          disabled={magicLoading || !promptText.trim()}
                          sx={{
                            borderRadius: "18px",
                            textTransform: "none",
                            px: 2.5,
                          }}
                        >
                          {magicLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            "Update"
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Grow>
              )}
            </Popper>
          );
        })()}

      {/* TipTap table + column-resize styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .tiptap table { border-collapse: collapse; table-layout: auto; margin: 0; }
        .tiptap table:not(.sig-table) { overflow: hidden; }
        .tiptap table.sig-table { overflow: visible; }
        .tiptap td, .tiptap th { min-width: 1em; border: none; vertical-align: top; box-sizing: border-box; position: relative; }
        .tiptap table:not(.sig-table) td, .tiptap table:not(.sig-table) th { border: 1px solid #ddd; padding: 4px 8px; }
        .tiptap .selectedCell:after { z-index: 2; position: absolute; content: ""; left: 0; right: 0; top: 0; bottom: 0; background: rgba(200,200,255,0.4); pointer-events: none; }
        .tiptap .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background-color: #adf; pointer-events: none; }
      `,
        }}
      />
    </Box>
  );
};

export default QuillEditor;
